import { Controller } from "@hotwired/stimulus";
import {
  bindAnchoredPanelReposition,
  positionAnchoredPanel,
  resetAnchoredPanel,
  unbindAnchoredPanelReposition,
} from "../utils/anchored_panel_position";

/**
 * Multi-select filter dropdown with type-to-search. Matches the supply-chain
 * filter dropdown chrome (summary, clear, GET submit) but loads options from a
 * JSON blob and renders matches on demand so large option sets stay usable.
 */
export default class extends Controller {
  static targets = [
    "button",
    "panel",
    "summary",
    "chevron",
    "clear",
    "wrap",
    "search",
    "optionsList",
    "hiddenInputs",
    "pasteInput",
    "applyPaste",
    "csvFile",
  ];

  static values = {
    paramKey: String,
    minQueryLength: { type: Number, default: 1 },
    maxResults: { type: Number, default: 50 },
    allowIdPaste: { type: Boolean, default: false },
    pasteParamKey: String,
    idFormat: { type: String, default: "facility" },
  };

  connect() {
    this.open = false;
    this._repositionPanel = null;
    this.allOptions = this.parseOptions();
    this.selectedValues = new Set(this.readInitialSelectedValues());
    this._onMouseDown = this._onMouseDown.bind(this);
    document.addEventListener("mousedown", this._onMouseDown);
    this.syncHiddenInputs();
    this.syncSummary();
  }

  normalizeValue(value) {
    return String(value ?? "").trim();
  }

  isSelected(value) {
    return this.selectedValues.has(this.normalizeValue(value));
  }

  disconnect() {
    document.removeEventListener("mousedown", this._onMouseDown);
    this.unbindPanelReposition();
  }

  parseOptions() {
    const base64 = this.element.dataset.searchableFilterDropdownOptionsBase64;
    if (!base64) return [];

    try {
      const json = this.decodeBase64Utf8(base64);
      const parsed = JSON.parse(json);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error("searchable-filter-dropdown: failed to parse options", e);
      return [];
    }
  }

  // atob() returns a Latin-1 string; Ruby's Base64.strict_encode64(JSON) is UTF-8.
  decodeBase64Utf8(base64) {
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder("utf-8").decode(bytes);
  }

  readInitialSelectedValues() {
    if (!this.hasHiddenInputsTarget) return [];

    const name = `${this.paramKeyValue}[]`;

    return Array.from(
      this.hiddenInputsTarget.querySelectorAll(
        `input[type="hidden"][name="${name}"]`,
      ),
    )
      .map((input) => this.normalizeValue(input.value))
      .filter(Boolean);
  }

  toggle(event) {
    event.preventDefault();
    if (!this.hasPanelTarget) return;

    this.open = !this.open;
    this.panelTarget.classList.toggle("hidden", !this.open);
    this.buttonTarget.setAttribute("aria-expanded", String(this.open));

    if (this.hasChevronTarget) {
      this.chevronTarget.classList.toggle("rotate-180", this.open);
    }

    if (this.open) {
      this.renderOptions();
      this.positionPanel();
      if (this.hasSearchTarget) {
        this.searchTarget.value = "";
        this.searchTarget.focus({ preventScroll: true });
      } else if (this.hasPasteInputTarget) {
        this.pasteInputTarget.focus({ preventScroll: true });
      }
    } else {
      this.resetPanelPosition();
    }
  }

  clear(event) {
    event.stopPropagation();
    event.preventDefault();
    this.selectedValues.clear();
    this.removePasteField();
    this.syncHiddenInputs();
    this.syncSummary();
    this.renderOptions();
    this.submitForm();
  }

  applyPaste(event) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.allowIdPasteValue || !this.hasPasteInputTarget) return;

    const raw = this.pasteInputTarget.value.trim();
    if (!raw) return;

    this.applyPasteValue(raw);
  }

  applyPasteValue(raw) {
    this.setPasteField(raw);
    this.submitForm();
  }

  // --- CSV upload / drag & drop ---------------------------------------------

  // Only the first CSV column is used. Matrix IDs are integers; facility IDs are
  // 2 letters + 6 hex chars (e.g. CA008A99, optional leading #). The separator
  // set mirrors the backend paste resolver (comma/semicolon/tab).
  csvConfig() {
    if (this.idFormatValue === "integer") {
      return {
        regex: /^\d+$/,
        hint: "numeric Matrix IDs (e.g. 12345)",
        label: "Matrix IDs",
      };
    }

    return {
      regex: /^#?[A-Za-z]{2}[0-9A-Fa-f]{6}$/,
      hint: "facility IDs (e.g. CA008A99)",
      label: "facilities",
    };
  }

  uploadCsv(event) {
    event.preventDefault();
    if (this.hasCsvFileTarget) this.csvFileTarget.click();
  }

  onCsvFile(event) {
    const file = event.target.files && event.target.files[0];
    event.target.value = ""; // let the same file be re-selected later
    this.processCsvFile(file);
  }

  onDragOver(event) {
    event.preventDefault();
    this.setDropHighlight(event.currentTarget, true);
  }

  onDragLeave(event) {
    this.setDropHighlight(event.currentTarget, false);
  }

  onDrop(event) {
    event.preventDefault();
    this.setDropHighlight(event.currentTarget, false);
    const file =
      event.dataTransfer &&
      event.dataTransfer.files &&
      event.dataTransfer.files[0];
    this.processCsvFile(file);
  }

  setDropHighlight(zone, active) {
    zone.classList.toggle("bg-[#7BB6B3]", active);
  }

  async processCsvFile(file) {
    if (!file || !this.allowIdPasteValue || !this.hasPasteInputTarget) return;

    const isCsv =
      /\.csv$/i.test(file.name) || (file.type || "").includes("csv");
    if (!isCsv) {
      window.alert("Please upload a .csv file.");
      return;
    }

    const { label, hint } = this.csvConfig();

    let text;
    try {
      text = await file.text();
    } catch (_error) {
      window.alert("Could not read the CSV file.");
      return;
    }

    const { ids, empty } = this.extractCsvIds(text);

    if (empty) {
      window.alert("The CSV file is empty.");
      return;
    }

    if (ids.length === 0) {
      window.alert(
        `No valid ${label} found in the CSV. Expected ${hint} in the first column.`,
      );
      return;
    }

    const value = ids.join(", ");
    this.pasteInputTarget.value = value;
    this.applyPasteValue(value);
  }

  extractCsvIds(text) {
    const { regex } = this.csvConfig();

    const values = text
      .replace(/^﻿/, "")
      .split(/\r?\n/)
      .map((line) => line.replace(/[;\t]/g, ",").split(",")[0] || "")
      .map((cell) => cell.replace(/^"|"$/g, "").trim())
      .filter((cell) => cell.length > 0);

    if (values.length === 0) return { ids: [], empty: true };

    // Drop the first row when it isn't a valid ID — almost certainly a header.
    if (!regex.test(values[0])) values.shift();

    const ids = [...new Set(values.filter((value) => regex.test(value)))];
    return { ids, empty: false };
  }

  onPasteKeydown(event) {
    if (event.key !== "Enter" || event.shiftKey) return;

    event.preventDefault();
    this.applyPaste(event);
  }

  onSearchInput() {
    this.renderOptions();
  }

  onSearchKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      this.closePanel();
    }
  }

  onOptionClick(event) {
    const row = event.target.closest("[data-option-value]");
    if (!row) return;

    event.preventDefault();
    const value = this.normalizeValue(row.dataset.optionValue);
    if (!value) return;

    if (this.selectedValues.has(value)) {
      this.selectedValues.delete(value);
    } else {
      this.selectedValues.add(value);
    }

    this.syncHiddenInputs();
    this.syncSummary();
    this.renderOptions();
    this.submitForm();
  }

  renderOptions() {
    if (!this.hasOptionsListTarget) return;

    const query = this.hasSearchTarget
      ? this.searchTarget.value.toLowerCase().trim()
      : "";
    const minLen = this.minQueryLengthValue;
    const maxResults = this.maxResultsValue;
    const selectedOptions = this.allOptions.filter((opt) =>
      this.isSelected(opt.value),
    );

    let html = "";

    if (selectedOptions.length > 0) {
      html += this.renderSectionHeading("Selected");
      html += selectedOptions
        .map((opt) => this.renderOptionRow(opt, true))
        .join("");
    }

    const unselected = this.allOptions.filter(
      (opt) => !this.isSelected(opt.value),
    );
    let matches = unselected;

    if (query.length >= minLen) {
      matches = unselected.filter(
        (opt) =>
          opt.label.toLowerCase().includes(query) ||
          opt.value.toLowerCase().includes(query),
      );
    }

    const totalMatches = matches.length;
    matches = matches.slice(0, maxResults);

    if (matches.length === 0) {
      if (query.length >= minLen) {
        html += this.renderHint("No results found");
      }
    } else {
      html += this.renderSectionHeading(
        query.length >= minLen ? "Results" : "Facilities",
      );
      html += matches.map((opt) => this.renderOptionRow(opt, false)).join("");

      if (totalMatches > maxResults) {
        html += this.renderHint(
          `Showing ${maxResults} of ${totalMatches} — refine your search`,
        );
      }
    }

    this.optionsListTarget.innerHTML = html;

    if (this.open) {
      positionAnchoredPanel(this.panelTarget, this.anchorElement());
    }
  }

  renderSectionHeading(text) {
    return `<p class="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-modern-400">${this.escapeHtml(text)}</p>`;
  }

  renderHint(text) {
    return `<p class="px-4 py-3 text-sm italic text-gray-modern-400">${this.escapeHtml(text)}</p>`;
  }

  renderOptionRow(opt, selected) {
    const checkClass = selected ? "" : "hidden";
    const rowClass = selected ? " bg-brand-25" : "";

    return `
      <button
        type="button"
        data-option-value="${this.escapeHtml(opt.value)}"
        data-action="click->searchable-filter-dropdown#onOptionClick"
        class="flex w-full cursor-pointer items-center justify-between gap-2 px-4 py-2.5 text-left text-sm text-gray-modern-700 hover:bg-gray-modern-50${rowClass}"
      >
        <span class="min-w-0 flex-1">${this.escapeHtml(opt.label)}</span>
        <svg
          class="h-4 w-4 shrink-0 text-brand-600 ${checkClass}"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M16.667 5L7.5 14.167L3.333 10" />
        </svg>
      </button>
    `;
  }

  syncHiddenInputs() {
    if (!this.hasHiddenInputsTarget) return;

    const name = `${this.paramKeyValue}[]`;
    this.hiddenInputsTarget.innerHTML = "";

    this.selectedValues.forEach((value) => {
      const normalized = this.normalizeValue(value);
      if (!normalized) return;

      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = normalized;
      this.hiddenInputsTarget.appendChild(input);
    });
  }

  selectedCount() {
    return Array.from(this.selectedValues)
      .map((value) => this.normalizeValue(value))
      .filter(Boolean).length;
  }

  summaryText(count = this.selectedCount()) {
    if (count === 0) return "All";
    if (count === 1) return "1 selected";

    return `${count} selected`;
  }

  syncSummary() {
    const selected = this.selectedCount();
    const text = this.summaryText(selected);

    this.summaryTarget.textContent = text;

    if (this.hasWrapTarget) {
      this.wrapTarget.toggleAttribute("data-active", selected > 0);
    }
    if (this.hasClearTarget) {
      this.clearTarget.classList.toggle("hidden", selected === 0);
    }
    if (this.hasChevronTarget) {
      this.chevronTarget.classList.toggle("hidden", selected > 0);
    }
  }

  setPasteField(value) {
    const key = this.pasteParamKeyValue;
    if (!key) return;

    this.removePasteField();

    const form = this.element.closest("form");
    if (!form) return;

    const input = document.createElement("input");
    input.type = "hidden";
    input.name = key;
    input.value = value;
    input.dataset.searchableFilterDropdownPaste = "true";
    form.appendChild(input);
  }

  removePasteField() {
    const form = this.element.closest("form");
    if (!form) return;

    form
      .querySelectorAll("input[data-searchable-filter-dropdown-paste]")
      .forEach((el) => el.remove());

    if (this.hasPasteInputTarget) {
      this.pasteInputTarget.value = "";
    }
  }

  submitForm() {
    const form = this.element.closest("form");
    if (form) form.requestSubmit();
  }

  closePanel() {
    this.open = false;
    if (this.hasPanelTarget) {
      this.panelTarget.classList.add("hidden");
    }
    this.resetPanelPosition();
    this.buttonTarget.setAttribute("aria-expanded", "false");
    if (this.hasChevronTarget) {
      this.chevronTarget.classList.remove("rotate-180");
    }
    if (this.hasSearchTarget) {
      this.searchTarget.value = "";
    }
  }

  anchorElement() {
    return this.hasWrapTarget ? this.wrapTarget : this.buttonTarget;
  }

  positionPanel() {
    if (!this.hasPanelTarget) return;

    positionAnchoredPanel(this.panelTarget, this.anchorElement());
    if (this._repositionPanel) return;

    this._repositionPanel = bindAnchoredPanelReposition(
      this.panelTarget,
      this.anchorElement(),
    );
  }

  resetPanelPosition() {
    if (this.hasPanelTarget) {
      resetAnchoredPanel(this.panelTarget);
    }
    this.unbindPanelReposition();
  }

  unbindPanelReposition() {
    unbindAnchoredPanelReposition(this._repositionPanel);
    this._repositionPanel = null;
  }

  _onMouseDown(event) {
    if (
      !this.open ||
      !this.hasPanelTarget ||
      this.element.contains(event.target)
    ) {
      return;
    }
    this.closePanel();
  }

  escapeHtml(text) {
    return String(text)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }
}
